
// ...existing code...
import React, { useEffect, useState, useMemo, useRef } from "react";

import FullCalendar from "@fullcalendar/react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction"; // Import the interaction plugin
import axios from "axios";
import deleteBooking from "./deleteBooking"; // Auth-aware delete helper
import { useToast } from "../../Contexts/ToastContext"; // Toasts
import CalendarViewManageBookingDetails from "./CalendarViewManageBookingDetails"; // Import the CalendarViewManageBookingDetails component
import { Modal, OverlayTrigger, Tooltip, Form } from "react-bootstrap"; // Import Modal, OverlayTrigger, Tooltip, and Form from react-bootstrap
import { getAllBookingsWithDetails, OrderDetailsDTO } from "./bookingService";
import { getContinuousAvailabilityWarnings } from "./bookingUtils";

import { DateSelectArg, EventInput } from "@fullcalendar/core";
import CalendarSimpleBookingForm from "./CalendarSimpleBookingForm";
import ChaletOccupancyOverview from "./ChaletOccupancyOverview";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { toDateOnlyString } from "../../Utilities/date";

const ManagerCalendarView = ({
  events,
  onEventChange,
}: {
  events: any[];
  onEventChange: () => void;
}) => {
  const { showToast } = useToast();
  // Restore handleEventResize function inside component
  const handleEventResize = async (resizeInfo: any) => {
    const updatedEvent = {
      ...resizeInfo.event.extendedProps.details,
      arrivalDate: resizeInfo.event.startStr,
      departureDate: resizeInfo.event.endStr,
    };

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/Booking/updateBooking`,
        updatedEvent
      );
      onEventChange();
    } catch (error) {
      console.error("Error updating booking:", error);
    }
  };
  const apiUrl = process.env.REACT_APP_API_URL || "defaultApiUrl";

  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetailsDTO | null>(
    null
  );
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);
  const [showViewBookingModal, setShowViewBookingModal] = useState(false);
  const [editingNote, setEditingNote] = useState<{
    bookingId: number;
    text: string;
  } | null>(null);
  // Removed unused error state
  const [orders, setOrders] = useState<OrderDetailsDTO[]>([]);
  const [processedEvents, setProcessedEvents] = useState<EventInput[]>([]);
  const [viewDates, setViewDates] = useState<{ start: Date; end: Date } | null>(
    null
  );
  // Store availability data from the calendar-availability endpoint (per-day breakdown)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [dailyAvailability, setDailyAvailability] = useState<Map<string, { bigAvailable: number; bigTotal: number; smallAvailable: number; smallTotal: number }>>(new Map());
  // Whether a SINGLE chalet of each type is free for the ENTIRE currently-viewed week (the
  // continuous/"book this whole stay" check), as opposed to dailyAvailability above which only
  // tells you a day-by-day snapshot. A day can show "1 ledig" while this is still false, because
  // same-day turnover means a DIFFERENT chalet is the free one on different days - no single
  // chalet actually spans every night shown. Without this distinction the grid looks continuously
  // bookable even when nothing in it actually is, which is exactly the "shuffle chalets around"
  // confusion reported by managers.
  const [continuousAvailability, setContinuousAvailability] = useState<{ big: boolean; small: boolean }>({ big: true, small: true });

  // Retrieve the user's language settings
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null,
  });
  const formatDate = (date: string) => {
    return format(new Date(date), "dd/MM/yyyy", { locale: sv });
  };

  const fetchBookings = async () => {
    try {
      const response = await getAllBookingsWithDetails();
      console.log("Fetched response:", response);
      if (response.success) {
        if (Array.isArray(response.data)) {
          console.log("Setting orders data:", response.data);
          setOrders(response.data);
          console.log("Orders data set successfully");
        } else {
          console.error("Response data is not an array:", response.data);
        }
        // setError(""); // Removed unused error state
      } else {
        // setError(response.message); // Removed unused error state
      }
    } catch (error: any) {
      // setError(error.message); // Removed unused error state
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []); // Run only once when the component mounts

  // When parent provides refreshed events, also refresh bookings to keep counts accurate
  useEffect(() => {
    if (events && events.length >= 0) {
      fetchBookings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  // Fetch availability from the calendar-availability endpoint for the entire view range in ONE call.
  // The backend now returns dailyAvailability[] with per-day breakdowns that properly handle
  // same-day turnover (departures on a day don't block new arrivals starting that day).
  // It ALSO returns availabilityByType[].availableChalets, which is the TRUE continuous check
  // (is a single physical chalet free for every night in the range, not just some of them) - we
  // return that alongside the per-day map so the grid can tell the two apart.
  const fetchDailyAvailability = React.useCallback(async (start: Date, end: Date): Promise<{
    dailyMap: Map<string, { bigAvailable: number; bigTotal: number; smallAvailable: number; smallTotal: number }>;
    continuous: { big: boolean; small: boolean };
  }> => {
    const availabilityMap = new Map<string, { bigAvailable: number; bigTotal: number; smallAvailable: number; smallTotal: number }>();
    let continuousBig = true;
    let continuousSmall = true;

    const startKey = toDateOnlyString(start)!;
    const endKey = toDateOnlyString(end)!;

    try {
      console.log(`[CALENDAR-AVAIL] Fetching range ${startKey} → ${endKey}`);
      const response = await axios.get(`${apiUrl}/PackageBooking/calendar-availability`, {
        params: { startDate: startKey, endDate: endKey },
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
      });

      const data = response.data as any;
      console.log(`[CALENDAR-AVAIL] RAW RESPONSE:`, JSON.stringify(data));

      // Continuous (whole-range, single-chalet) availability check
      if (data?.availabilityByType && Array.isArray(data.availabilityByType)) {
        for (const item of data.availabilityByType) {
          const ct = String(item.chaletType).toLowerCase().trim();
          const continuousAvail = typeof item.availableChalets === "number" ? item.availableChalets : 0;
          if (ct === "big" || ct === "0" || item.chaletType === 0) continuousBig = continuousAvail > 0;
          else if (ct === "small" || ct === "1" || item.chaletType === 1) continuousSmall = continuousAvail > 0;
        }
      }

      // --- Parse the new dailyAvailability array (preferred) ---
      if (data?.dailyAvailability && Array.isArray(data.dailyAvailability)) {
        for (const dayEntry of data.dailyAvailability) {
          const dateKey = String(dayEntry.date).split("T")[0];
          let bigAvail = 0, bigTotal = 0, smallAvail = 0, smallTotal = 0;

          if (Array.isArray(dayEntry.byType)) {
            for (const t of dayEntry.byType) {
              const ct = String(t.chaletType).toLowerCase().trim();
              const avail = typeof t.available === "number" ? t.available : 0;
              const total = typeof t.total === "number" ? t.total : 0;

              if (ct === "big" || ct === "0" || t.chaletType === 0) {
                bigAvail = avail;
                bigTotal = total;
              } else if (ct === "small" || ct === "1" || t.chaletType === 1) {
                smallAvail = avail;
                smallTotal = total;
              }
            }
          }

          console.log(`[CALENDAR-AVAIL] ${dateKey}: Big=${bigAvail}/${bigTotal}, Small=${smallAvail}/${smallTotal}`);
          availabilityMap.set(dateKey, { bigAvailable: bigAvail, bigTotal, smallAvailable: smallAvail, smallTotal });
        }
      }
      // --- Fallback: old availabilityByType aggregate (apply same number to every day) ---
      else if (data?.availabilityByType && Array.isArray(data.availabilityByType)) {
        console.warn(`[CALENDAR-AVAIL] No dailyAvailability, falling back to weekly aggregate`);
        let bigAvail = 0, bigTotal = 0, smallAvail = 0, smallTotal = 0;

        for (const item of data.availabilityByType) {
          const ct = String(item.chaletType).toLowerCase().trim();
          const avail = typeof item.availableChalets === "number" ? item.availableChalets : (typeof item.available === "number" ? item.available : 0);
          const total = typeof item.totalChalets === "number" ? item.totalChalets : (typeof item.total === "number" ? item.total : 0);

          if (ct === "big" || ct === "0" || item.chaletType === 0) { bigAvail = avail; bigTotal = total; }
          else if (ct === "small" || ct === "1" || item.chaletType === 1) { smallAvail = avail; smallTotal = total; }
        }

        // Apply the same aggregate to every day in range
        for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
          const dk = new Date(d);
          dk.setHours(0, 0, 0, 0);
          availabilityMap.set(toDateOnlyString(dk)!, { bigAvailable: bigAvail, bigTotal, smallAvailable: smallAvail, smallTotal });
        }
      }
    } catch (error: any) {
      console.error(`[CALENDAR-AVAIL] ERROR:`, error?.response?.data || error.message);
      // On error, fill every day with 0 (fully booked) - safer default
      for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
        const dk = new Date(d);
        dk.setHours(0, 0, 0, 0);
        availabilityMap.set(toDateOnlyString(dk)!, { bigAvailable: 0, bigTotal: 0, smallAvailable: 0, smallTotal: 0 });
      }
      continuousBig = false;
      continuousSmall = false;
    }

    return { dailyMap: availabilityMap, continuous: { big: continuousBig, small: continuousSmall } };
  }, [apiUrl]);

  useEffect(() => {
    if (orders.length === 0 || !viewDates) {
      return; // Don't run until we have orders and a date range
    }
    
    // First, transform the nested orders data into a flat list of calendar events.
    const bookingEvents: EventInput[] = orders.flatMap((order) =>
      order.bookings.map((booking) => {
        let color = "#6c757d";
        const chaletTypeNormalized = String(booking.chaletType ?? "").toLowerCase();
        if (booking.chalet && booking.chalet.isBlocked) {
          color = "#ffc107";
        } else if (chaletTypeNormalized === "small" || chaletTypeNormalized === "1") {
          color = "#ed6c73";
        } else if (chaletTypeNormalized === "big" || chaletTypeNormalized === "0") {
          color = "#7499af";
        }
        return {
          id: `${order.orderNumber}-${booking.bookingId}`,
          title: `${order.customerName || "Booking"} - ${booking.chaletType}`,
          start: booking.arrivalDate,
          end: booking.departureDate,
          allDay: true,
          backgroundColor: color,
          borderColor: color,
          extendedProps: {
            details: {
              ...booking,
              orderNumber: order.orderNumber,
              customerName: order.customerName,
              guestNames: booking.guestNames || [],
              isBusinessUser: order.isBusinessUser,
              bookingDescription: order.bookingDescription,
            },
            sortOrder: 0,
            sortKey: `0-${booking.bookingId}`,
          },
        };
      })
    );

    // Use the calendar-availability endpoint with dailyAvailability for accurate per-day counts
    // This properly handles same-day turnover (departures don't block new arrivals)
    const generateAvailabilityEvents = async () => {
      const { dailyMap: availabilityData, continuous } = await fetchDailyAvailability(viewDates.start, viewDates.end);
      setDailyAvailability(availabilityData);
      setContinuousAvailability(continuous);

      const generatedEvents: EventInput[] = [];

      const startOfDay = (date: Date) => {
        const newDate = new Date(date);
        newDate.setHours(0, 0, 0, 0);
        return newDate;
      };

      for (
        let d = new Date(viewDates.start);
        d < viewDates.end;
        d.setDate(d.getDate() + 1)
      ) {
        const currentDate = startOfDay(new Date(d));
        const dateKey = toDateOnlyString(currentDate)!;

        // Get per-day availability from the backend (properly handles same-day turnover)
        const dayAvailability = availabilityData.get(dateKey) || { bigAvailable: 0, bigTotal: 0, smallAvailable: 0, smallTotal: 0 };
        const { bigAvailable, bigTotal, smallAvailable, smallTotal } = dayAvailability;
        const bigBooked = bigTotal - bigAvailable;
        const smallBooked = smallTotal - smallAvailable;

        console.log(`Calendar rendering ${dateKey}: Large=${bigAvailable}/${bigTotal}, Small=${smallAvailable}/${smallTotal}`);

        // --- Large chalets ---
        if (bigAvailable > 0 && continuous.big) {
          // A single chalet genuinely covers every night of the whole visible week - safe to
          // read as "bookable for the whole stay".
          generatedEvents.push({
            id: `available-large-${dateKey}`,
            title: `Stor stuga: ${bigBooked} bokad · ${bigAvailable} ledig`,
            start: currentDate,
            allDay: true,
            backgroundColor: "#bbdefb",
            borderColor: "#90caf9",
            classNames: ["available-event"],
            editable: false,
            extendedProps: { isPlaceholder: true, sortOrder: 1, sortKey: `1` },
          });
        } else if (bigAvailable > 0 && !continuous.big) {
          // This day alone has a free chalet, but NO single chalet is free for the entire
          // visible week - it's a DIFFERENT chalet free on different days (turnover between
          // other guests), not one continuously bookable unit. Flag this distinctly so
          // managers don't read "1 ledig" on every day as proof the whole week is bookable.
          generatedEvents.push({
            id: `available-large-${dateKey}`,
            title: `Stor stuga: ${bigAvailable} ledig idag ⚠ (ej hela veckan)`,
            start: currentDate,
            allDay: true,
            backgroundColor: "#ffe0b2",
            borderColor: "#ffb74d",
            classNames: ["available-event", "turnover-only-event"],
            editable: false,
            extendedProps: { isPlaceholder: true, isTurnoverOnly: true, sortOrder: 1, sortKey: `1` },
          });
        } else {
          generatedEvents.push({
            id: `booked-large-${dateKey}`,
            title: `Stor stuga: Fullbokat${bigTotal > 0 ? ` (${bigTotal}/${bigTotal})` : ''}`,
            start: currentDate,
            allDay: true,
            backgroundColor: "#ffcdd2",
            borderColor: "#e57373",
            classNames: ["fully-booked-event"],
            editable: false,
            extendedProps: { isPlaceholder: true, isFullyBooked: true, sortOrder: 1, sortKey: `1` },
          });
        }

        // --- Small chalets ---
        if (smallAvailable > 0 && continuous.small) {
          generatedEvents.push({
            id: `available-small-${dateKey}`,
            title: `Liten stuga: ${smallBooked} bokad · ${smallAvailable} ledig`,
            start: currentDate,
            allDay: true,
            backgroundColor: "#c8e6c9",
            borderColor: "#a5d6a7",
            classNames: ["available-event"],
            editable: false,
            extendedProps: { isPlaceholder: true, sortOrder: 2, sortKey: `2` },
          });
        } else if (smallAvailable > 0 && !continuous.small) {
          generatedEvents.push({
            id: `available-small-${dateKey}`,
            title: `Liten stuga: ${smallAvailable} ledig idag ⚠ (ej hela veckan)`,
            start: currentDate,
            allDay: true,
            backgroundColor: "#ffe0b2",
            borderColor: "#ffb74d",
            classNames: ["available-event", "turnover-only-event"],
            editable: false,
            extendedProps: { isPlaceholder: true, isTurnoverOnly: true, sortOrder: 2, sortKey: `2` },
          });
        } else {
          generatedEvents.push({
            id: `booked-small-${dateKey}`,
            title: `Liten stuga: Fullbokat${smallTotal > 0 ? ` (${smallTotal}/${smallTotal})` : ''}`,
            start: currentDate,
            allDay: true,
            backgroundColor: "#ffcdd2",
            borderColor: "#e57373",
            classNames: ["fully-booked-event"],
            editable: false,
            extendedProps: { isPlaceholder: true, isFullyBooked: true, sortOrder: 2, sortKey: `2` },
          });
        }
      }

      // Combine booking events with availability events
      const allEvents = [...bookingEvents, ...generatedEvents];
      setProcessedEvents(allEvents);
    };

    generateAvailabilityEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, viewDates, apiUrl, fetchDailyAvailability]);



  const handleSaveNote = async () => {
    if (!editingNote) return;

    try {
      await axios.put(`${apiUrl}/Booking/${editingNote.bookingId}/note`, {
        note: editingNote.text,
      });
      setEditingNote(null); // Exit editing mode
      fetchBookings(); // Re-fetch all bookings to get the latest data
    } catch (error) {
      console.error("Error updating booking note:", error);
      // Optionally, show an error message to the user
    }
  };

  const handleEventClick = (clickInfo: any) => {
    if (clickInfo.event.extendedProps.isPlaceholder) {
      return; // Do nothing for placeholder events
    }
    setSelectedEvent(clickInfo.event);
    const order = orders.find(
      (order) =>
        order.orderNumber === clickInfo.event.extendedProps.details.orderNumber
    );
    if (order) {
      setSelectedOrder(order); // Set the entire order object
    }
    console.log("Event clicked:", clickInfo.event);
  };

  const handleDateClick = (dateClickInfo: any) => {
    console.log("Date clicked:", dateClickInfo.dateStr);
    setSelectedEvent(null); // Deselect the selected event
  };

  const handleEventDrop = async (dropInfo: any) => {
    const updatedEvent = {
      ...dropInfo.event.extendedProps.details,
      arrivalDate: dropInfo.event.startStr,
      departureDate: dropInfo.event.endStr,
    };

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/Booking/updateBooking`,
        updatedEvent
      );
      onEventChange();
    } catch (error) {
      console.error("Error updating booking:", error);
    }
    // Remove global bookingEvents sort; instead, sort all events per day by sortKey

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/Booking/updateBooking`,
        updatedEvent
      );
      onEventChange();
    } catch (error) {
      console.error("Error updating booking:", error);
    }
  };

  const [pendingDelete, setPendingDelete] = useState<{bookingId: number, eventId: string} | null>(null);

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    // Extract real bookingId from extendedProps (id may be composite order-booking)
    const bookingId = selectedEvent.extendedProps?.details?.bookingId;
    if (!bookingId) {
      console.error("Could not resolve bookingId for deletion", selectedEvent.id);
      showToast("Deletion failed: booking ID missing", "error");
      return;
    }
    
    // Set pending delete state to show confirmation toast
    setPendingDelete({ bookingId, eventId: selectedEvent.id });
    showToast("Delete booking? Click 'Confirm Delete' to proceed or wait 10 seconds to cancel.", "info", 10000);
  };

  const confirmDeleteEvent = async () => {
    if (!pendingDelete) return;
    
    try {
      await deleteBooking(pendingDelete.bookingId); // includes Authorization header
      showToast("Booking deleted successfully", "success");
      onEventChange && onEventChange();
      fetchBookings();
      setSelectedEvent(null);
      setPendingDelete(null);
    } catch (err: any) {
      console.error("Error deleting booking:", err);
      if (err?.response?.status === 401) {
        showToast("Unauthorized (401): check login / role", "error");
      } else {
        showToast("Delete failed: " + (err.message || "Unknown error"), "error");
      }
      setPendingDelete(null);
    }
  };

  const cancelDelete = () => {
    setPendingDelete(null);
    showToast("Deletion cancelled", "info");
  };

  const handleDateSelect = (selectInfo: DateSelectArg) => {
    const { start } = selectInfo;
    let { end } = selectInfo;
    console.log("Selected date range:", start, end);
    // FullCalendar selects one-day by default when clicking a single day (end is next day).
    // Default to 1 night when the selection is effectively a single day — manager can adjust freely.
    const oneDayMs = 24 * 60 * 60 * 1000;
    const durationDays = end && start ? Math.round((end.getTime() - start.getTime()) / oneDayMs) : 0;
    if (start && (!end || durationDays <= 1)) {
      const defaultEnd = new Date(start);
      defaultEnd.setDate(defaultEnd.getDate() + 1);
      end = defaultEnd;
    }
    setDateRange({ startDate: start, endDate: end });
    setShowAddBookingModal(true); // Show the modal

    // The day cells only show PER-DAY availability, which can look "available" even when no
    // single chalet is free for the whole selected stay (turnover between different units on
    // different days). Warn the manager immediately so a later booking rejection isn't a surprise.
    getContinuousAvailabilityWarnings(apiUrl, start, end).then((warnings) => {
      for (const warning of warnings) {
        showToast(`⚠️ ${warning.chaletType} chalets: ${warning.note}`, "info", 15000);
      }
    });
  };

  const handleCloseAddBookingModal = () => {
    setShowAddBookingModal(false); // Close the add booking modal
  };

  const handleCloseViewBookingModal = () => {
    setShowViewBookingModal(false); // Close the view booking modal
  };

  const handleViewEditBooking = (event: any) => {
    setSelectedEvent(event);
    setShowViewBookingModal(true);
  };

  const handleMouseEnter = (info: any) => {
    // console.log("Event Mouse Enter:", info.event);
  };

  const handleMouseLeave = (info: any) => {
    //  console.log("Event Mouse Leave:", info.event);
  };

  const renderTooltip = (props: any) => (
    <Tooltip id="button-tooltip" {...props}>
      {/* {props.description}
      <br />
      {props.price}
      <br /> */}
      {props.isBusinessUser &&
        `Pre-Booking: ${props.preBooking ? "Yes" : "No"}`}
    </Tooltip>
  );

  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth()
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );

  // The calendar view is "dayGridWeek" - a single week, not a month. When the dropdown jumps
  // to day 1 of a chosen month/year, that day often falls inside a week that actually starts
  // in the PREVIOUS month/year (e.g. 1 Jan 2027 falls in the week of Mon 28 Dec 2026). FullCalendar's
  // datesSet then fires with that week's start date, which the sync-on-navigate logic below would
  // read as "December 2026" and silently snap the dropdowns back - exactly the "jumps back to
  // December 2026 and won't move to another year" bug. Suppress that one re-sync whenever the
  // navigation was user-initiated from the dropdowns themselves; the dropdown value is already
  // correct and must not be overwritten by the week's rounded start date.
  const isDropdownNavigation = useRef(false);

  const handleMonthChange = (event: React.ChangeEvent<any>) => {
    const newMonth = parseInt((event.target as HTMLSelectElement).value, 10);
    setSelectedMonth(newMonth);
    isDropdownNavigation.current = true;
    const newDate = new Date(selectedYear, newMonth, 1);
    calendarRef.current?.getApi().gotoDate(newDate);
  };

  const handleYearChange = (event: React.ChangeEvent<any>) => {
    const newYear = parseInt((event.target as HTMLSelectElement).value, 10);
    setSelectedYear(newYear);
    isDropdownNavigation.current = true;
    const newDate = new Date(newYear, selectedMonth, 1);
    calendarRef.current?.getApi().gotoDate(newDate);
  };

  const calendarRef = useRef<FullCalendar>(null);

  const calendarEvents = useMemo(() => {
    return processedEvents.map((event) => ({
      ...event,
      display: "block", // Forces a clean display for events
      backgroundColor:
        selectedEvent && selectedEvent.id === event.id
          ? "#ffcc00"
          : event.backgroundColor, // Change color of selected event
      borderColor:
        selectedEvent && selectedEvent.id === event.id
          ? "#ff0000"
          : event.borderColor, // Add border to selected event
    }));
  }, [processedEvents, selectedEvent]);

  return (
    <div className="container-fluid d-flex justify-content-center mt-2 pt-2 mb-5 pb-5">
      <div className="calendarView__section">
        <div className="manage-bookings-buttons">
          <Form className="form-inline">
            <Form.Group controlId="formMonthSelect" className="mr-2">
              <div className="dropdown-with-icon">
                <Form.Control
                  as="select"
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  className="custom-select"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i} value={i}>
                      {new Date(0, i).toLocaleString("default", {
                        month: "long",
                      })}
                    </option>
                  ))}
                </Form.Control>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="dropdown-icon"
                />
              </div>
            </Form.Group>
            <Form.Group controlId="formYearSelect" className="ml-2">
              <div className="dropdown-with-icon">
                <Form.Control
                  as="select"
                  value={selectedYear}
                  onChange={handleYearChange}
                  className="custom-select"
                >
                  {Array.from({ length: 10 }, (_, i) => (
                    <option key={i} value={new Date().getFullYear() - 5 + i}>
                      {new Date().getFullYear() - 5 + i}
                    </option>
                  ))}
                </Form.Control>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="dropdown-icon"
                />
              </div>
            </Form.Group>
            <Form.Group className="ml-3">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  // Trigger both parent refresh and local bookings fetch
                  try { onEventChange && onEventChange(); } catch {}
                  fetchBookings();
                }}
              >
                Refresh
              </button>
            </Form.Group>
          </Form>
        </div>

        {(!continuousAvailability.big || !continuousAvailability.small) && (
          <div className="alert alert-warning py-2 px-3 mb-2" style={{ fontSize: "13px" }}>
            <span style={{ display: "inline-block", width: 12, height: 12, backgroundColor: "#ffe0b2", border: "1px solid #ffb74d", marginRight: 6, verticalAlign: "middle" }}></span>
            <strong>Orange = ledig endast den dagen, inte hela veckan.</strong> Ingen enskild stuga
            {!continuousAvailability.big && !continuousAvailability.small ? " (stor eller liten)" : continuousAvailability.big ? " (liten)" : " (stor)"} är
            ledig alla nätter i den visade veckan - det är olika stugor som blir lediga olika dagar
            (gästbyte). Öppna &quot;Boka&quot; och dra det exakta datumintervallet för en säker kontroll innan bokning.
          </div>
        )}

        <ChaletOccupancyOverview apiUrl={apiUrl} viewStart={viewDates ? viewDates.start : null} />

        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]} // Add the interaction plugin
          initialView="dayGridWeek"
          locale="sv"
          selectable={true} // Enable date selection
          select={handleDateSelect} // Add the select event handler
          datesSet={(dateInfo) => {
            setViewDates({ start: dateInfo.start, end: dateInfo.end });
            // Keep the Month/Year dropdowns in sync when the manager navigates via the
            // calendar's own prev/next buttons (previously they only updated when the
            // dropdowns themselves were changed, so they silently went stale otherwise).
            // But skip this sync when the navigation came FROM the dropdowns - see the
            // isDropdownNavigation comment above for why the week view's rounded start
            // date must not be allowed to overwrite the dropdown's own correct value.
            if (isDropdownNavigation.current) {
              isDropdownNavigation.current = false;
              return;
            }
            const current = dateInfo.view.currentStart;
            setSelectedMonth(current.getMonth());
            setSelectedYear(current.getFullYear());
          }}
          eventMouseEnter={handleMouseEnter}
          eventMouseLeave={handleMouseLeave}
          events={calendarEvents}
          eventOrder="extendedProps.sortKey"
          eventContent={(eventInfo) => {
            if (eventInfo.event.extendedProps.isPlaceholder) {
              return (
                <div
                  style={{
                    padding: "5px",
                    color: "black",
                    backgroundColor: eventInfo.event.backgroundColor,
                    border: `2px solid ${
                      eventInfo.event.borderColor || "transparent"
                    }`,
                  }}
                >
                  <strong>{eventInfo.event.title}</strong>
                </div>
              );
            }
            // Default rendering for actual booking events
            return (
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip({
                  description: `Blocked Chalets: ${eventInfo.event.extendedProps.description}`,
                  price: `Total Cost: ${eventInfo.event.extendedProps.price}`,
                  preBooking: eventInfo.event.extendedProps.preBooking,
                  isBusinessUser:
                    eventInfo.event.extendedProps.details.isBusinessUser,
                })}
              >
                <div
                  style={{
                    padding: "5px",
                    color: "black",
                    backgroundColor:
                      selectedEvent && selectedEvent.id === eventInfo.event.id
                        ? "#ffcc00"
                        : eventInfo.event.backgroundColor, // Change background color
                    border: `2px solid ${
                      eventInfo.event.borderColor || "transparent"
                    }`, // Apply border color
                  }}
                >
                  {(() => {
                    const details = eventInfo.event.extendedProps.details;
                    interface BookingDetails {
                      guestNames?: string[];
                      customerName?: string;
                      chaletType?: string;
                      arrivalDate: string;
                      departureDate: string;
                      orderNumber?: string | number;
                      isBusinessUser?: boolean;
                      bookingDescription?: string;
                      chaletId?: number;
                      bookingId?: number;
                      bookingSmallNote?: string;
                      [key: string]: any;
                    }

                    const guestNames: string[] = ((details as BookingDetails).guestNames || []).filter((g: string) => g && g.trim() && g.toLowerCase() !== 'unknown guest');
                    const hasGuests = guestNames.length > 0;
                    const title = hasGuests
                      ? guestNames.slice(0, 2).join(', ') + (guestNames.length > 2 ? ', ...' : '')
                      : details.customerName || 'Booking';

                    return (
                      <>
                        <strong>
                          {title}
                        </strong>
                        <span style={{ marginLeft: "5px", fontSize: "0.8rem" }}>
                          {` - ${details.chaletType}`}
                        </span>
                        <br />
                        <div style={{ fontSize: "0.8rem" }}>
                          {hasGuests && (
                            <>
                              Booked by: {details.customerName}
                              <br />
                            </>
                          )}
                          {formatDate(details.arrivalDate)} - {formatDate(details.departureDate)}
                        </div>
                      </>
                    );
                  })()}
                  <div style={{ fontSize: "0.8rem", marginTop: "4px" }}>
                    {editingNote?.bookingId ===
                    eventInfo.event.extendedProps.details.bookingId ? (
                      <div
                        className="note-editor-container"
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <textarea
                          value={editingNote?.text}
                          onChange={(e) => {
                            if (editingNote) {
                              setEditingNote({
                                ...editingNote,
                                text: e.target.value,
                              });
                            }
                          }}
                          onClick={(e) => e.stopPropagation()} // Prevent calendar event click
                        />
                        <div className="note-editor-buttons">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveNote();
                            }}
                            className="btn btn-success btn-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingNote(null);
                            }}
                            className="btn btn-secondary btn-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <strong>
                          <i className="fa-solid fa-note-sticky"></i>
                        </strong>{" "}
                        <span>
                          {eventInfo.event.extendedProps.details
                            .bookingSmallNote || "..."}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const eventDetails =
                              eventInfo.event.extendedProps.details;

                            // Find the parent order using orderNumber from the event details
                            const parentOrder = orders.find(
                              (o) => o.orderNumber === eventDetails.orderNumber
                            );

                            if (parentOrder) {
                              // Find the specific booking within that order
                              const booking = parentOrder.bookings.find(
                                (b) =>
                                  b.chaletId === eventDetails.chaletId &&
                                  new Date(b.arrivalDate).getTime() ===
                                    new Date(eventDetails.arrivalDate).getTime()
                              );

                              if (booking) {
                                setEditingNote({
                                  bookingId: booking.bookingId,
                                  text: booking.bookingSmallNote || "",
                                });
                              }
                            }
                          }}
                          className="btn-edit-note"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                  {selectedEvent && selectedEvent.id === eventInfo.event.id && (
                    <div
                      className="event-action-buttons"
                      style={{ marginTop: "6px" }}
                    >
                      <button
                        //className="btn btn-primary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewEditBooking(eventInfo.event);
                        }}
                      >
                        View/Edit Booking
                      </button>
                      {pendingDelete?.eventId === eventInfo.event.id ? (
                        <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                          <button
                            style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '2px 6px', borderRadius: '3px', fontSize: '0.8rem' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDeleteEvent();
                            }}
                          >
                            Confirm Delete
                          </button>
                          <button
                            style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '2px 6px', borderRadius: '3px', fontSize: '0.8rem' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              cancelDelete();
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          //  className="btn btn-danger btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEvent(eventInfo.event);
                            handleDeleteEvent();
                          }}
                        >
                          Delete Booking
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </OverlayTrigger>
            );
          }}
          headerToolbar={{
            left: "prev,next",
            center: "title",
            right: "dayGridWeek",
          }}
          firstDay={1}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
        />
        {selectedOrder && (
          <Modal
            show={showViewBookingModal}
            onHide={handleCloseViewBookingModal}
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title>Booking Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedOrder && selectedEvent?.extendedProps?.details && (
                <CalendarViewManageBookingDetails
                  booking={selectedEvent.extendedProps.details}
                  order={selectedOrder}
                  onClose={handleCloseViewBookingModal}
                  fetchBookings={fetchBookings}
                />
              )}
            </Modal.Body>
            <Modal.Footer>
              <div className="booking-actions">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleCloseViewBookingModal}
                >
                  {" "}
                  <i className="fas fa-times"></i> Close
                </button>
              </div>
            </Modal.Footer>
          </Modal>
        )}
        <Modal
          show={showAddBookingModal}
          onHide={handleCloseAddBookingModal}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>New Booking</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <CalendarSimpleBookingForm
              dateRange={dateRange}
              setDateRange={setDateRange}
              apiUrl={apiUrl}
            />
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-secondary"
              onClick={handleCloseAddBookingModal}
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default ManagerCalendarView;
